import { createContext, useLayoutEffect, useRef, type RefObject } from 'react'

/**
 * The dialog surface's class lists and enter handover, in a module of their own so the modal slot's outlet and the slot
 * routes' pending skeletons (components/custom/dialog-skeleton.tsx) reach them without pulling base-ui's dialog into
 * the entry.
 */
export const DIALOG_OVERLAY_CLASS =
  'fixed inset-0 isolate z-50 bg-black/10 duration-100 supports-backdrop-filter:backdrop-blur-xs data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0'

/**
 * The scroller the popup sits in: the whole viewport, so a dialog taller than the screen scrolls like a page, its
 * scrollbar at the screen's edge (docs/notes/app/dialog-routes.md). No padding: a popup's sticky header or footer
 * sticks at the scroller's padding edge, and a gutter there showed content scrolling past outside it.
 */
export const DIALOG_VIEWPORT_CLASS = 'fixed inset-0 z-50 overflow-y-auto overscroll-contain'

let mountedViewports = 0

/**
 * Ref for a modal's scroller: `<html data-modal-viewport>` while one is mounted, which app.css hands the page's
 * scrollbar to. An attribute and not `html:has(...)`, which restyled the whole document on every DOM change anywhere
 * (docs/notes/app/document-restyle.md).
 */
export function markModalViewport(node: HTMLElement | null) {
  if (!node) return
  mountedViewports += 1
  document.documentElement.setAttribute('data-modal-viewport', '')
  return () => {
    mountedViewports -= 1
    if (mountedViewports === 0) document.documentElement.removeAttribute('data-modal-viewport')
  }
}

/**
 * The box inside the scroller: at least the viewport's height, with the gutter above and below. The popup's `m-auto`
 * centres a short one and starts a tall one at the gutter; its enter and exit animate its transform, so nothing may
 * translate it for centring.
 */
export const DIALOG_FRAME_CLASS = 'flex min-h-full py-4'

export const DIALOG_POPUP_CLASS =
  'relative m-auto grid w-full max-w-[calc(100%-2rem)] gap-4 rounded-xl bg-background p-4 text-sm ring-1 ring-foreground/10 duration-100 outline-none sm:max-w-sm data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95'

/**
 * On a skeleton: its exit holds its last frame. The real surface is unmounted by base-ui the moment its exit ends; a
 * skeleton stays until its owner's timer, and without this it painted at full opacity again in between.
 */
export const SKELETON_EXIT_HOLD = 'data-closed:fill-mode-forwards'

/**
 * A skeleton's enter, handed to the real surface that replaces it. The two draw the same `enter` keyframes, so the
 * surface carries on with ITS OWN `enter` started at the skeleton's start time on the document timeline: one
 * animation across the swap, at whatever point the swap lands. Swapped mid-enter, it continues from the skeleton's
 * opacity and scale; swapped after, the enter is already over and the surface lands at rest. A flag that switched
 * the surface's enter off landed it at rest mid-enter, a visible jump (docs/notes/app/loading-states.md).
 */
export interface EnterHandover {
  /** The skeleton's `enter` start time, set as it unmounts while open; null once taken, or if it never painted. */
  startTime: CSSNumberish | null
}

export function createEnterHandover(): EnterHandover {
  return { startTime: null }
}

/** The handover of the skeleton on screen for this surface; null where no skeleton stands in (a nested dialog). */
export const DialogEnterContext = createContext<EnterHandover | null>(null)

function enterAnimation(element: Element): Animation | undefined {
  return element.getAnimations().find((animation) => (animation as CSSAnimation).animationName === 'enter')
}

/**
 * The skeleton side, on mount: returns what its unmount calls to record where its enter started. The animation is
 * caught NOW because a finished one drops out of `getAnimations()`, yet keeps its start time.
 */
export function trackEnter(element: Element | null): () => CSSNumberish | null {
  const enter = element && typeof element.getAnimations === 'function' ? enterAnimation(element) : undefined
  return () => enter?.startTime ?? null
}

/**
 * The skeleton side: while it is the open surface, its unmount is the swap, so it leaves its enter's start time in
 * the handover. An unmount after a close leaves nothing, or the next open would land at rest.
 */
export function useRecordEnter(ref: RefObject<Element | null>, handover: EnterHandover | null, open: boolean) {
  const openRef = useRef(open)
  useLayoutEffect(() => {
    openRef.current = open
  })
  useLayoutEffect(() => {
    const startTime = trackEnter(ref.current)
    return () => {
      if (handover && openRef.current) handover.startTime = startTime()
    }
  }, [ref, handover])
}

/** The surface side, as its elements mount: their `enter` takes the skeleton's start time. Taken once. */
export function continueEnter(handover: EnterHandover | null, elements: (Element | null)[]) {
  const startTime = handover?.startTime
  if (!handover || startTime == null) return
  handover.startTime = null
  for (const element of elements) {
    if (!element || typeof element.getAnimations !== 'function') continue
    const enter = enterAnimation(element)
    if (enter) enter.startTime = startTime
  }
}
